/**
 * Sprint 6 Design System — premium Linear/Stripe-inspired
 * Single-file ergonomic export to keep imports tidy.
 */
import { motion, useReducedMotion } from 'framer-motion';
import type { HTMLAttributes, ReactNode } from 'react';
import { ArrowDown, ArrowUp, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// =====================================================================
// Sémantique modules
// =====================================================================
export type ModuleVariant =
  | 'dashboard'
  | 'crm'
  | 'formation_b2b'
  | 'formation_b2c'
  | 'finances'
  | 'echeances'
  | 'marketing'
  | 'params';

const MODULE_GRADIENTS: Record<ModuleVariant, string> = {
  dashboard: 'from-slate-50 to-slate-100',
  crm: 'from-blue-50 to-indigo-100',
  formation_b2b: 'from-violet-50 to-purple-100',
  formation_b2c: 'from-pink-50 to-rose-100',
  finances: 'from-orange-50 to-amber-100',
  echeances: 'from-emerald-50 to-teal-100',
  marketing: 'from-fuchsia-50 to-pink-100',
  params: 'from-zinc-50 to-slate-100',
};

const MODULE_HALOS: Record<ModuleVariant, string> = {
  dashboard: 'bg-slate-300/40',
  crm: 'bg-indigo-300/40',
  formation_b2b: 'bg-violet-300/40',
  formation_b2c: 'bg-rose-300/40',
  finances: 'bg-amber-300/40',
  echeances: 'bg-emerald-300/40',
  marketing: 'bg-fuchsia-300/40',
  params: 'bg-zinc-300/40',
};

const MODULE_ACCENT: Record<ModuleVariant, string> = {
  dashboard: 'text-slate-900',
  crm: 'text-blue-600',
  formation_b2b: 'text-violet-600',
  formation_b2c: 'text-rose-500',
  finances: 'text-orange-600',
  echeances: 'text-emerald-600',
  marketing: 'text-fuchsia-600',
  params: 'text-zinc-700',
};

// =====================================================================
// PageContainer
// =====================================================================
export function PageContainer({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div className={cn('mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8', className)} {...rest}>
      {children}
    </div>
  );
}

// =====================================================================
// ContextHero — bandeau gradient sémantique avec halos diffus
// =====================================================================
export interface ContextHeroProps {
  variant: ModuleVariant;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  actions?: ReactNode;
  kpis?: { label: string; value: ReactNode }[];
}

export function ContextHero({
  variant,
  title,
  subtitle,
  icon,
  actions,
  kpis,
}: ContextHeroProps): JSX.Element {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={reduce ? undefined : { opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        'relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br p-6 shadow-xl shadow-slate-900/5 sm:p-8',
        MODULE_GRADIENTS[variant],
      )}
    >
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full blur-3xl',
          MODULE_HALOS[variant],
        )}
      />
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full opacity-50 blur-3xl',
          MODULE_HALOS[variant],
        )}
      />
      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 flex-1">
          {icon && (
            <div
              className={cn(
                'mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/70 ring-1 ring-slate-200/80 backdrop-blur',
                MODULE_ACCENT[variant],
              )}
            >
              {icon}
            </div>
          )}
          <h1 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
          {subtitle && (
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-700">{subtitle}</p>
          )}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
      {kpis && kpis.length > 0 && (
        <div className="relative mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {kpis.map((k, i) => (
            <div
              key={i}
              className="rounded-xl bg-white/60 p-3 ring-1 ring-slate-200/80 backdrop-blur"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {k.label}
              </div>
              <div className="mt-0.5 text-lg font-black tabular-nums text-slate-900">{k.value}</div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

// =====================================================================
// KpiCard
// =====================================================================
export interface KpiCardProps {
  label: string;
  value: ReactNode;
  unit?: string;
  variant?: ModuleVariant;
  trend?: number; // %
  icon?: ReactNode;
  loading?: boolean;
  hint?: string;
}

export function KpiCard({
  label,
  value,
  unit,
  variant = 'dashboard',
  trend,
  icon,
  loading,
  hint,
}: KpiCardProps): JSX.Element {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={reduce ? undefined : { opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
          {label}
        </div>
        {icon && <div className={cn('shrink-0', MODULE_ACCENT[variant])}>{icon}</div>}
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        {loading ? (
          <SkeletonShimmer variant="kpi" />
        ) : (
          <>
            <span className="text-3xl font-black tabular-nums text-slate-900">{value}</span>
            {unit && <span className="text-sm font-semibold text-slate-500">{unit}</span>}
          </>
        )}
      </div>
      {!loading && trend != null && (
        <div
          className={cn(
            'mt-1 inline-flex items-center gap-1 text-xs font-semibold',
            trend >= 0 ? 'text-emerald-600' : 'text-rose-600',
          )}
        >
          {trend >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
          {Math.abs(trend).toFixed(1)}%
        </div>
      )}
      {hint && !loading && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </motion.div>
  );
}

// =====================================================================
// StatusBadge — universel toutes-modules
// =====================================================================
export type UniversalStatus =
  | 'paye'
  | 'gagne'
  | 'regle'
  | 'fait'
  | 'en_cours'
  | 'en_attente'
  | 'planifie'
  | 'en_retard'
  | 'a_payer'
  | 'critique'
  | 'perdu'
  | 'dette'
  | 'brouillon'
  | 'annule';

const STATUS_TONE: Record<
  UniversalStatus,
  { bg: string; text: string; ring: string; dot: string }
> = {
  paye: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
    dot: 'bg-emerald-500',
  },
  gagne: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
    dot: 'bg-emerald-500',
  },
  regle: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
    dot: 'bg-emerald-500',
  },
  fait: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
    dot: 'bg-emerald-500',
  },
  en_cours: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    ring: 'ring-amber-200',
    dot: 'bg-amber-500',
  },
  en_attente: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    ring: 'ring-amber-200',
    dot: 'bg-amber-500',
  },
  planifie: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    ring: 'ring-amber-200',
    dot: 'bg-amber-500',
  },
  en_retard: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    ring: 'ring-orange-200',
    dot: 'bg-orange-500',
  },
  a_payer: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    ring: 'ring-orange-200',
    dot: 'bg-orange-500',
  },
  critique: { bg: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-200', dot: 'bg-rose-500' },
  perdu: { bg: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-200', dot: 'bg-rose-500' },
  dette: { bg: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-200', dot: 'bg-rose-500' },
  brouillon: {
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    ring: 'ring-slate-200',
    dot: 'bg-slate-400',
  },
  annule: {
    bg: 'bg-slate-50',
    text: 'text-slate-500',
    ring: 'ring-slate-200',
    dot: 'bg-slate-400',
  },
};

export interface StatusBadgeProps {
  status: UniversalStatus;
  label?: string;
  variant?: 'default' | 'large';
  withDot?: boolean;
}

export function StatusBadge({
  status,
  label,
  variant = 'default',
  withDot = false,
}: StatusBadgeProps): JSX.Element {
  const tone = STATUS_TONE[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-semibold ring-1',
        tone.bg,
        tone.text,
        tone.ring,
        variant === 'large' ? 'px-3 py-1 text-xs' : 'px-2 py-0.5 text-[10px]',
      )}
    >
      {withDot && (
        <span
          className={cn(
            'h-1.5 w-1.5 rounded-full',
            tone.dot,
            ['en_cours', 'en_attente', 'planifie'].includes(status) && 'animate-pulse',
          )}
        />
      )}
      {label ?? status.replace(/_/g, ' ')}
    </span>
  );
}

// =====================================================================
// DataCard
// =====================================================================
export interface DataCardProps {
  variant?: 'default' | 'pastel' | 'sub';
  module?: ModuleVariant;
  title?: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  footer?: ReactNode;
  interactive?: boolean;
  loading?: boolean;
  className?: string;
  children?: ReactNode;
}

export function DataCard({
  variant = 'default',
  module: mod = 'dashboard',
  title,
  subtitle,
  trailing,
  footer,
  interactive,
  loading,
  className,
  children,
}: DataCardProps): JSX.Element {
  const base =
    variant === 'pastel'
      ? cn(
          'rounded-2xl bg-gradient-to-br p-5 shadow-md ring-1 ring-slate-200/60',
          MODULE_GRADIENTS[mod],
        )
      : variant === 'sub'
        ? 'rounded-xl bg-slate-50/40 p-4 ring-1 ring-slate-100'
        : 'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm';
  return (
    <motion.div
      whileHover={interactive ? { y: -2 } : undefined}
      transition={{ type: 'spring', stiffness: 300, damping: 22 }}
      className={cn(
        base,
        interactive && 'cursor-pointer hover:border-slate-300 hover:shadow-lg',
        className,
      )}
    >
      {(title || trailing) && (
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {title && (
              <div className="line-clamp-1 text-base font-bold text-slate-800">{title}</div>
            )}
            {subtitle && (
              <div className="mt-0.5 line-clamp-1 text-xs text-slate-500">{subtitle}</div>
            )}
          </div>
          {trailing && <div className="shrink-0">{trailing}</div>}
        </div>
      )}
      {loading ? <SkeletonShimmer variant="card" /> : children}
      {footer && (
        <div className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-600">{footer}</div>
      )}
    </motion.div>
  );
}

// =====================================================================
// SkeletonShimmer
// =====================================================================
export function SkeletonShimmer({
  variant = 'card',
  count = 1,
  className,
}: {
  variant?: 'card' | 'list' | 'table' | 'kpi' | 'hero';
  count?: number;
  className?: string;
}): JSX.Element {
  const block = (
    <div
      className={cn(
        'animate-pulse rounded-md bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100',
        variant === 'kpi' && 'h-8 w-32',
        variant === 'card' && 'h-20 w-full',
        variant === 'list' && 'h-12 w-full',
        variant === 'table' && 'h-9 w-full',
        variant === 'hero' && 'h-32 w-full',
        className,
      )}
    />
  );
  if (count <= 1) return block;
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i}>{block}</div>
      ))}
    </div>
  );
}

// =====================================================================
// MoneyAmount
// =====================================================================
const MONEY_FORMATTER = new Intl.NumberFormat('fr-DZ', {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

export interface MoneyAmountProps {
  amount: number | null | undefined;
  currency?: 'DZD' | 'EUR' | 'USD';
  variant?: 'default' | 'large' | 'kpi' | 'inline';
  showSign?: boolean;
  withColor?: boolean;
  className?: string;
}

export function MoneyAmount({
  amount,
  currency = 'DZD',
  variant = 'default',
  showSign = false,
  withColor = false,
  className,
}: MoneyAmountProps): JSX.Element {
  const v = amount ?? 0;
  const sign = showSign && v > 0 ? '+' : '';
  const symbol = currency === 'DZD' ? 'DA' : currency === 'EUR' ? '€' : '$';
  const sizeClass =
    variant === 'kpi'
      ? 'text-3xl font-black'
      : variant === 'large'
        ? 'text-xl font-bold'
        : variant === 'inline'
          ? 'text-sm font-semibold'
          : 'text-sm font-semibold';
  const colorClass = withColor ? (v >= 0 ? 'text-emerald-700' : 'text-rose-700') : 'text-slate-900';
  return (
    <span className={cn('tabular-nums', sizeClass, colorClass, className)}>
      {sign}
      {MONEY_FORMATTER.format(v)} {symbol}
    </span>
  );
}

// =====================================================================
// ActionButton
// =====================================================================
export interface ActionButtonProps extends Omit<
  HTMLAttributes<HTMLButtonElement>,
  'onAnimationStart' | 'onDrag' | 'onDragStart' | 'onDragEnd'
> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'gradient';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  module?: ModuleVariant;
  type?: 'button' | 'submit' | 'reset';
  icon?: ReactNode;
}

export function ActionButton({
  variant = 'primary',
  size = 'md',
  loading,
  disabled,
  module: mod = 'dashboard',
  icon,
  type = 'button',
  className,
  children,
  ...rest
}: ActionButtonProps): JSX.Element {
  const sizeClass =
    size === 'sm'
      ? 'h-8 px-3 text-xs'
      : size === 'lg'
        ? 'h-12 px-6 text-base'
        : 'h-10 px-4 text-sm';
  const variantClass =
    variant === 'primary'
      ? 'bg-slate-900 text-white hover:bg-slate-800 shadow-sm'
      : variant === 'gradient'
        ? cn(
            'bg-gradient-to-br text-white shadow-md hover:shadow-lg',
            mod === 'formation_b2b' && 'from-violet-600 to-purple-600',
            mod === 'formation_b2c' && 'from-rose-500 to-pink-500',
            mod === 'crm' && 'from-blue-600 to-indigo-600',
            mod === 'finances' && 'from-orange-500 to-amber-500',
            mod === 'echeances' && 'from-emerald-600 to-teal-600',
            (mod === 'dashboard' || mod === 'params' || mod === 'marketing') &&
              'from-slate-700 to-slate-900',
          )
        : variant === 'secondary'
          ? 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50'
          : variant === 'danger'
            ? 'bg-rose-600 text-white hover:bg-rose-700 shadow-sm'
            : 'text-slate-700 hover:bg-slate-100';
  return (
    <motion.button
      type={type}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 400, damping: 28 }}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        sizeClass,
        variantClass,
        className,
      )}
      {...(rest as Record<string, unknown>)}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {children}
    </motion.button>
  );
}

// =====================================================================
// EmptyStateModern
// =====================================================================
export interface EmptyStateModernProps {
  variant?: ModuleVariant;
  title: string;
  description?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  ctaSecondaryLabel?: string;
  onCtaSecondaryClick?: () => void;
  icon?: ReactNode;
}

export function EmptyStateModern({
  variant = 'dashboard',
  title,
  description,
  ctaLabel,
  onCtaClick,
  ctaSecondaryLabel,
  onCtaSecondaryClick,
  icon,
}: EmptyStateModernProps): JSX.Element {
  return (
    <div
      className={cn(
        'mx-auto flex max-w-xl flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-200 bg-gradient-to-br p-10 text-center',
        MODULE_GRADIENTS[variant],
      )}
    >
      {icon && (
        <div
          className={cn(
            'flex h-14 w-14 items-center justify-center rounded-2xl bg-white/80 ring-1 ring-slate-200',
            MODULE_ACCENT[variant],
          )}
        >
          {icon}
        </div>
      )}
      <div>
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
        {description && (
          <p className="mt-1 max-w-sm text-sm leading-relaxed text-slate-700">{description}</p>
        )}
      </div>
      {(ctaLabel || ctaSecondaryLabel) && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {ctaLabel && (
            <ActionButton variant="gradient" module={variant} onClick={onCtaClick}>
              {ctaLabel}
            </ActionButton>
          )}
          {ctaSecondaryLabel && (
            <ActionButton variant="secondary" onClick={onCtaSecondaryClick}>
              {ctaSecondaryLabel}
            </ActionButton>
          )}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// DateBadge
// =====================================================================
export interface DateBadgeProps {
  date: string | Date | null | undefined;
  format?: 'short' | 'long' | 'relative';
  className?: string;
}

export function DateBadge({ date, format: f = 'short', className }: DateBadgeProps): JSX.Element {
  if (!date) return <span className={cn('text-slate-400', className)}>—</span>;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (f === 'long') {
    return (
      <span className={cn('text-xs text-slate-700', className)}>
        {d.toLocaleDateString('fr-DZ', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        })}
      </span>
    );
  }
  if (f === 'relative') {
    const days = Math.round((d.getTime() - Date.now()) / 86400000);
    const label = days === 0 ? "aujourd'hui" : days > 0 ? `dans ${days}j` : `il y a ${-days}j`;
    return <span className={cn('text-xs text-slate-700', className)}>{label}</span>;
  }
  return (
    <span className={cn('text-xs tabular-nums text-slate-700', className)}>
      {d.toLocaleDateString('fr-DZ', { day: '2-digit', month: '2-digit', year: '2-digit' })}
    </span>
  );
}

// =====================================================================
// SectionTitle
// =====================================================================
export function SectionTitle({
  children,
  trailing,
  className,
}: {
  children: ReactNode;
  trailing?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn('flex items-end justify-between gap-3', className)}>
      <h2 className="text-xl font-bold text-slate-800">{children}</h2>
      {trailing}
    </div>
  );
}
